import { type Environment, PRODUCTION } from '@shared/environments';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

export const getCurrentEnvironmentOrDefault = (): Environment =>
  (process.env.ENV as Environment) || PRODUCTION;

export const createFeatureFlag = <F extends Record<string, boolean>>(
  environmentConfig: Readonly<Record<Environment, F>>,
) => {
  type FeatureFlags = Extract<keyof F, string>;
  const environment = getCurrentEnvironmentOrDefault();

  type Ctx = {
    isEnabled: (flag: FeatureFlags) => boolean;
  };

  const FeatureFlagContext = createContext<Ctx | null>(null);

  const Provider = ({ children }: { children: ReactNode }) => {
    // biome-ignore lint/correctness/useExhaustiveDependencies: the environment will never change
    const featureFlagState = useMemo(() => {
      const flagState = environmentConfig[environment];

      const shouldLogFeatureFlagState =
        environment !== PRODUCTION && process.env.NODE_ENV !== 'test';
      if (shouldLogFeatureFlagState) {
        console.table(flagState);
      }

      return flagState;
    }, []);

    const isEnabled = (flag: FeatureFlags) => featureFlagState[flag] ?? false;

    return (
      <FeatureFlagContext.Provider value={{ isEnabled }}>
        {children}
      </FeatureFlagContext.Provider>
    );
  };

  const useFeatureFlags = () => {
    const ctx = useContext(FeatureFlagContext);
    if (!ctx) {
      throw new Error('useFeatureFlags must be used within this Provider');
    }
    return ctx;
  };

  return { Provider, useFeatureFlags, FeatureFlagContext };
};
